/**
 * Umsortieren per Ziehen – mit Pointer Events, damit es auf dem iPad
 * funktioniert. Das HTML5-Drag-and-Drop von Desktop-Browsern reagiert
 * unter iOS nicht auf Beruehrungen.
 *
 * Gezogen wird nur am Griff (.drag-handle), damit normales Scrollen
 * der Liste weiter moeglich bleibt.
 */

export interface ReorderOptions {
  /** Elementauswahl des Ziehgriffs innerhalb eines Listeneintrags. */
  handleSelector?: string;
  /** Wird mit den Indizes aufgerufen, wenn losgelassen wurde. */
  onReorder: (from: number, to: number) => void;
}

export function makeReorderable(list: HTMLElement, opts: ReorderOptions): () => void {
  const handleSelector = opts.handleSelector ?? '.drag-handle';

  let items: HTMLElement[] = [];
  let heights: number[] = [];
  let centers: number[] = [];
  let gap = 0;
  let dragIndex = -1;
  let targetIndex = -1;
  let startY = 0;
  let dragged: HTMLElement | null = null;

  function measure(): void {
    items = Array.from(list.children) as HTMLElement[];
    const listTop = list.getBoundingClientRect().top;
    heights = items.map((el) => el.getBoundingClientRect().height);
    centers = items.map((el) => {
      const r = el.getBoundingClientRect();
      return r.top - listTop + r.height / 2;
    });
    gap = Number.parseFloat(getComputedStyle(list).rowGap || '0') || 0;
  }

  function onPointerDown(ev: PointerEvent): void {
    const handle = (ev.target as HTMLElement | null)?.closest(handleSelector);
    if (!handle || !list.contains(handle)) return;
    const item = handle.closest('li') as HTMLElement | null;
    if (!item) return;

    measure();
    dragIndex = items.indexOf(item);
    if (dragIndex < 0) return;

    targetIndex = dragIndex;
    dragged = item;
    startY = ev.clientY;
    ev.preventDefault();
    (handle as HTMLElement).setPointerCapture(ev.pointerId);
    item.classList.add('list-item--dragging');
    item.style.zIndex = '3';
    item.style.position = 'relative';
  }

  function onPointerMove(ev: PointerEvent): void {
    if (dragIndex < 0 || !dragged) return;
    const dy = ev.clientY - startY;
    dragged.style.transform = `translateY(${dy}px)`;

    // Neue Zielposition: wie viele andere Eintraege liegen oberhalb der Mitte?
    const center = (centers[dragIndex] as number) + dy;
    let t = 0;
    for (let i = 0; i < items.length; i += 1) {
      if (i === dragIndex) continue;
      if ((centers[i] as number) < center) t += 1;
    }
    targetIndex = t;

    // Die anderen Eintraege ausweichen lassen, damit die Luecke sichtbar ist.
    const shift = (heights[dragIndex] as number) + gap;
    items.forEach((el, i) => {
      if (i === dragIndex) return;
      let offset = 0;
      if (dragIndex < targetIndex && i > dragIndex && i <= targetIndex) offset = -shift;
      if (targetIndex < dragIndex && i >= targetIndex && i < dragIndex) offset = shift;
      el.style.transform = offset === 0 ? '' : `translateY(${offset}px)`;
      el.style.transition = 'transform 0.12s ease';
    });
  }

  function reset(): void {
    for (const el of items) {
      el.style.transform = '';
      el.style.transition = '';
      el.style.zIndex = '';
      el.style.position = '';
      el.classList.remove('list-item--dragging');
    }
    dragIndex = -1;
    targetIndex = -1;
    dragged = null;
  }

  function onPointerUp(): void {
    if (dragIndex < 0) return;
    const from = dragIndex;
    const to = targetIndex;
    reset();
    if (from !== to && to >= 0) opts.onReorder(from, to);
  }

  list.addEventListener('pointerdown', onPointerDown);
  list.addEventListener('pointermove', onPointerMove);
  list.addEventListener('pointerup', onPointerUp);
  list.addEventListener('pointercancel', onPointerUp);

  return () => {
    list.removeEventListener('pointerdown', onPointerDown);
    list.removeEventListener('pointermove', onPointerMove);
    list.removeEventListener('pointerup', onPointerUp);
    list.removeEventListener('pointercancel', onPointerUp);
  };
}

/** Verschiebt ein Element im Array und liefert eine neue Liste. */
export function moveItem<T>(arr: T[], from: number, to: number): T[] {
  const copy = arr.slice();
  const [item] = copy.splice(from, 1);
  if (item === undefined) return arr.slice();
  copy.splice(to, 0, item);
  return copy;
}
